import type {
  AnalyticsApi,
  AnalyticsEvent,
  AnalyticsEventAttributes,
} from '@backstage/core-plugin-api';
import { searchSession, type SearchSessionTracker } from './searchSession';
import type { SearchOutcome } from './sendSearchOutcome';

/** Idle time after the last keystroke before a search counts as committed. */
export const SEARCH_COMMIT_IDLE_MS = 1500;

/** Searches shorter than this (after trimming) are never sent. */
const MIN_TERM_LENGTH = 2;

export type SearchCommitReason =
  | 'idle'
  | 'select'
  | 'navigate'
  | 'close'
  | 'interaction'
  | 'unload';

type Pending = {
  event: AnalyticsEvent;
  timer: ReturnType<typeof setTimeout>;
};

type SurfaceState = {
  sessionId: string;
  lastCommittedTerm?: string;
  lastResultCount?: number;
};

export type SearchCommitAnalyticsOptions = {
  /** Shared search session, also used by the search API client. */
  session?: SearchSessionTracker;
  /** Reports select/abandon outcomes to the backend search query log. */
  sendOutcome?: (outcome: SearchOutcome) => void;
};

const RESULT_ACTIONS = new Set(['select', 'discover']);

/**
 * Resolves which search UI an event came from. The palette sets
 * `searchSurface` explicitly via `AnalyticsContext`; upstream surfaces (the
 * `/search` page, TechDocs in-page search) fall back to their extension id.
 */
function surfaceOf(event: AnalyticsEvent): string {
  const { searchSurface, extensionId } = event.context;
  if (typeof searchSurface === 'string' && searchSurface) return searchSurface;
  if (extensionId === 'page:search') return 'page';
  if (typeof extensionId === 'string' && extensionId.includes('techdocs')) {
    return 'techdocs';
  }
  return typeof extensionId === 'string' && extensionId
    ? extensionId
    : 'unknown';
}

function reasonFor(action: string): SearchCommitReason {
  if (RESULT_ACTIONS.has(action)) return 'select';
  if (action === 'navigate') return 'navigate';
  if (action === 'search_abandon') return 'close';
  return 'interaction';
}

/**
 * Turns per-keystroke search analytics into one event per committed search.
 *
 * Upstream `SearchContextProvider` emits a `search` event for every debounced
 * (200 ms) term change, so GA receives fragments such as `k`, `kub`, `kuber`.
 * This decorator buffers the latest `search` event per search surface and
 * forwards a single `search` event when the search is committed:
 *
 * - `idle`: no new term for {@link SEARCH_COMMIT_IDLE_MS}
 * - `select`: the user clicked a result (`select` / `discover`)
 * - `navigate`, `close` (`search_abandon`), `interaction`: any other event
 * - `unload`: the page is being hidden
 *
 * It also drops terms that are too short or repeat the last committed term,
 * and it skips a term that is a prefix of the last committed one (the user
 * deleting characters). It tags the committed search and the following
 * result click or abandonment with the shared `searchSessionId`, so a GA
 * funnel can join them. The search API client sends the same id with every
 * query, and the result click or abandonment is also reported to the backend
 * as a search outcome, which ends the session.
 *
 * Privacy: query text never leaves this decorator. The forwarded `search`
 * event has an empty subject (the GA4 module would otherwise send it as
 * `search_term`/`label`). Raw queries are stored server-side instead.
 */
export class SearchCommitAnalyticsApi implements AnalyticsApi {
  private readonly pending = new Map<string, Pending>();
  private readonly surfaces = new Map<string, SurfaceState>();

  private readonly session: SearchSessionTracker;
  private readonly sendOutcomeToBackend?: (outcome: SearchOutcome) => void;

  constructor(
    private readonly inner: AnalyticsApi,
    private readonly idleMs: number = SEARCH_COMMIT_IDLE_MS,
    options: SearchCommitAnalyticsOptions = {},
  ) {
    this.session = options.session ?? searchSession;
    this.sendOutcomeToBackend = options.sendOutcome;
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', () => this.flushAll('unload'));
    }
  }

  captureEvent(event: AnalyticsEvent): void {
    if (event.action === 'search') {
      this.buffer(event);
      return;
    }

    // Any other interaction commits pending searches first, so the search
    // always precedes the click/navigation it led to.
    this.flushAll(reasonFor(event.action));

    if (RESULT_ACTIONS.has(event.action) || event.action === 'search_abandon') {
      const surface = surfaceOf(event);
      const state = this.surfaces.get(surface);
      if (state) {
        this.inner.captureEvent(this.withSession(event, surface, state));
        this.sendOutcome(event, surface, state);
        // A result click or abandonment ends the search session.
        this.surfaces.delete(surface);
        this.session.end();
        return;
      }
    }

    this.inner.captureEvent(event);
  }

  private buffer(event: AnalyticsEvent) {
    const surface = surfaceOf(event);
    const existing = this.pending.get(surface);
    if (existing) clearTimeout(existing.timer);

    const term = event.subject.trim();
    if (term.length < MIN_TERM_LENGTH) {
      // The user cleared the box or typed a single character: nothing to send.
      this.pending.delete(surface);
      return;
    }

    const timer = setTimeout(() => this.flush(surface, 'idle'), this.idleMs);
    this.pending.set(surface, { event, timer });
  }

  private flushAll(reason: SearchCommitReason) {
    // Deleting the current entry while iterating a Map is safe.
    for (const surface of this.pending.keys()) {
      this.flush(surface, reason);
    }
  }

  private flush(surface: string, reason: SearchCommitReason) {
    const pending = this.pending.get(surface);
    if (!pending) return;
    clearTimeout(pending.timer);
    this.pending.delete(surface);

    const term = pending.event.subject.trim();
    const state = this.surfaces.get(surface) ?? {
      sessionId: this.session.current(),
    };
    const last = state.lastCommittedTerm;
    if (last && (term === last || last.startsWith(term))) {
      this.surfaces.set(surface, state);
      return;
    }
    const resultCount = pending.event.value;
    state.lastCommittedTerm = term;
    state.lastResultCount =
      typeof resultCount === 'number' ? resultCount : undefined;
    this.surfaces.set(surface, state);

    const attributes: AnalyticsEventAttributes = {
      ...pending.event.attributes,
      surface,
      commitReason: reason,
      termLength: term.length,
      searchSessionId: state.sessionId,
    };
    if (typeof resultCount === 'number') {
      attributes.resultCount = resultCount;
      attributes.zeroResults = resultCount === 0;
    }

    this.inner.captureEvent({ ...pending.event, subject: '', attributes });
  }

  private sendOutcome(
    event: AnalyticsEvent,
    surface: string,
    state: SurfaceState,
  ) {
    if (!this.sendOutcomeToBackend) return;
    const isAbandon = event.action === 'search_abandon';
    const resultType = event.attributes?.resultType;
    const resultCount =
      isAbandon && typeof event.value === 'number'
        ? event.value
        : state.lastResultCount;
    this.sendOutcomeToBackend({
      sid: state.sessionId,
      outcome: isAbandon ? 'abandon' : 'select',
      ...(!isAbandon && typeof event.value === 'number'
        ? { rank: event.value }
        : {}),
      ...(!isAbandon && typeof resultType === 'string' && resultType
        ? { resultType }
        : {}),
      ...(typeof resultCount === 'number' ? { resultCount } : {}),
      surface,
    });
  }

  private withSession(
    event: AnalyticsEvent,
    surface: string,
    state: SurfaceState,
  ): AnalyticsEvent {
    return {
      ...event,
      attributes: {
        ...event.attributes,
        surface,
        searchSessionId: state.sessionId,
      },
    };
  }
}
